import { Midi } from "@tonejs/midi";

// Performance recorder: buffers the player's live MIDI (note on/off + velocity, sustain
// CC64) in real wall-clock time and writes a Standard MIDI File via @tonejs/midi. Engine-
// independent — it never consults the playhead, so it keeps capturing across pause/play,
// mode changes, and song swaps. As-built → docs/architecture.md → "MIDI export".

interface RecNote {
  midi: number;
  startSec: number;
  durSec: number;
  velocity: number; // 0..1
}

export class MidiRecorder {
  private originMs = 0;
  private active = false;
  private readonly notes: RecNote[] = [];
  private readonly pedals: { sec: number; down: boolean }[] = [];
  /** Open notes by pitch (a single keyboard can't hold one pitch twice). */
  private readonly open = new Map<number, { startSec: number; velocity: number }>();

  get recording(): boolean {
    return this.active;
  }

  /** True when nothing worth exporting was captured (no completed notes). */
  get empty(): boolean {
    return this.notes.length === 0;
  }

  /** Notes captured so far (completed + in-flight) — for the recording indicator / E2E. */
  get noteCount(): number {
    return this.notes.length + this.open.size;
  }

  /** Begin a fresh recording; `timeMs` is the real-time origin (a MIDIMessageEvent /
   *  performance.now timestamp — they share one timebase). */
  start(timeMs: number): void {
    this.originMs = timeMs;
    this.active = true;
    this.notes.length = 0;
    this.pedals.length = 0;
    this.open.clear();
  }

  /** Stop, closing any in-flight notes at `timeMs`. */
  stop(timeMs: number): void {
    if (!this.active) return;
    const sec = this.sec(timeMs);
    for (const [midi, n] of this.open) {
      this.notes.push({
        midi,
        startSec: n.startSec,
        durSec: Math.max(0, sec - n.startSec),
        velocity: n.velocity,
      });
    }
    this.open.clear();
    this.active = false;
  }

  /** A musical note edge (already filtered of command/live-strip keys by the caller). */
  note(pitch: number, on: boolean, velocity: number, timeMs: number): void {
    if (!this.active) return;
    const sec = this.sec(timeMs);
    if (on) {
      // A re-trigger without an intervening note-off closes the prior note first.
      const prior = this.open.get(pitch);
      if (prior)
        this.notes.push({
          midi: pitch,
          startSec: prior.startSec,
          durSec: Math.max(0, sec - prior.startSec),
          velocity: prior.velocity,
        });
      this.open.set(pitch, { startSec: sec, velocity });
    } else {
      const n = this.open.get(pitch);
      if (!n) return;
      this.open.delete(pitch);
      this.notes.push({
        midi: pitch,
        startSec: n.startSec,
        durSec: Math.max(0, sec - n.startSec),
        velocity: n.velocity,
      });
    }
  }

  /** Sustain pedal (CC64) edge. */
  pedal(down: boolean, timeMs: number): void {
    if (!this.active) return;
    this.pedals.push({ sec: this.sec(timeMs), down });
  }

  /** Build the `.mid` bytes. `tempoBpm` sets the declared grid (note positions stay exact
   *  real-time — no quantization); `name` is the track name (the song's title, or free play). */
  toBytes(opts: { tempoBpm: number; name: string }): Uint8Array {
    const midi = new Midi();
    midi.header.setTempo(opts.tempoBpm);
    const track = midi.addTrack();
    track.name = opts.name;
    for (const n of this.notes) {
      track.addNote({ midi: n.midi, time: n.startSec, duration: n.durSec, velocity: n.velocity });
    }
    for (const p of this.pedals) {
      track.addCC({ number: 64, value: p.down ? 1 : 0, time: p.sec });
    }
    return midi.toArray();
  }

  private sec(timeMs: number): number {
    return Math.max(0, (timeMs - this.originMs) / 1000);
  }
}
